<script lang="ts">
export default {
  name: 'AccountOpportunities',
};

const ROW_CAP = 50;
</script>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { accountService } from '@/services/v2/AccountService';
import { opportunityStatus, weightedRevenueByProduct, summarize } from '@/utilities/Opportunities';
import type { Opportunity } from '@/types/Opportunity';
import { formatCurrency } from '@/utilities/Money';
import { formatDate } from '@/utilities/Format';
import { salesforceAccountUrl, salesforceUnavailableReason } from '@/utilities/Account';
import { CanAccessSalesforce } from '@/utilities/AuthUtils';
import store from '@/store';
import type { Account } from '@/types/Account';
import { paletteColors } from '@/utilities/Color';
import { FailedToLoadPage } from '@/utilities/Errors';
import PageSpinner from '@/components/v2/PageSpinner.vue';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import Alert from '@/components/v2/Alert.vue';

// Bound by AccountView's <router-view :account="account">.
const props = defineProps<{ account?: Account }>();

const route = useRoute();
const rows = ref<Opportunity[]>([]);
const loading = ref(true);
const error = ref(false);
const showAll = ref(false);

onMounted(async () => {
  try {
    rows.value = (await accountService.getOpportunitiesByAccount(Number(route.params.id))) ?? [];
  } catch (err) {
    console.error('Error loading opportunities:', err);
    rows.value = [];
    error.value = true;
  } finally {
    loading.value = false;
  }
});

const summary = computed(() => summarize(rows.value));
const slices = computed(() => weightedRevenueByProduct(rows.value));

const hasAny = computed(() => rows.value.length > 0);
// Whether there is anything to plot — NOT the same as "has open opportunities".
// weightedRevenueByProduct drops zero-valued groups, so open rows whose
// weightedRevenue is all null/0 yield empty slices while openCount > 0. The
// chart panel must still be swapped out in that case (an empty dataset handed
// to FusionCharts draws a blank canvas), but the message must not claim there
// are no open opportunities when there are — see chartEmptyMessage below.
const hasChartData = computed(() => slices.value.length > 0);
const chartEmptyMessage = computed(() =>
  summary.value.openCount === 0 ? 'No open opportunities' : 'No weighted revenue on open opportunities',
);
const hasSalesforceAccess = computed(() => CanAccessSalesforce(store.state.user));
const salesforceUrl = computed(() =>
  hasSalesforceAccess.value ? salesforceAccountUrl(props.account?.salesForceID) : null,
);
const salesforceLinkAttrs = computed(() =>
  salesforceUrl.value
    ? { href: salesforceUrl.value, target: '_blank', rel: 'noopener noreferrer' }
    : {
        role: 'link',
        'aria-disabled': true,
        title: salesforceUnavailableReason(hasSalesforceAccess.value, props.account?.salesForceID) ?? undefined,
      },
);

const visibleRows = computed(() => (showAll.value ? rows.value : rows.value.slice(0, ROW_CAP)));
const isCapped = computed(() => rows.value.length > ROW_CAP);

const openOpportunityNoun = computed(() => (summary.value.openCount === 1 ? 'opportunity' : 'opportunities'));

// Every slice is rendered — see the note in @/utilities/Opportunities on why
// formatDoughnutChartData is not used here.
const chartDataSource = computed(() => ({
  chart: {
    animation: '1',
    theme: 'cacs',
    showLegend: true,
    showBorder: false,
    decimals: 0,
    paletteColors,
    exportFilename: 'Pipeline by Product',
  },
  data: slices.value.map((s) => ({
    label: s.label,
    value: s.value,
    displayValue: formatCurrency(s.value, true),
    toolText: `<b>${s.label}</b>: ${formatCurrency(s.value, true)} ($percentValue)`,
  })),
}));

const statusClass = (o: Opportunity): string => {
  switch (opportunityStatus(o)) {
    case 'Won':
      return 'badge badge-sm badge-success badge-soft';
    case 'Lost':
      return 'badge badge-sm badge-error badge-soft';
    default:
      return 'badge badge-sm badge-info badge-soft';
  }
};

const dash = (v: string | number | null | undefined): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);
</script>

<template>
  <PageSpinner v-if="loading" />

  <Alert
    v-else-if="error"
    data-testid="opportunities-error"
    class="mx-4 mt-2 w-1/2"
    type="error">
    {{ FailedToLoadPage }}
  </Alert>

  <EmptyDisplay
    v-else-if="!hasAny"
    data-testid="opportunities-empty"
    bordered
    title="No opportunities"
    text="This account has no opportunities from Salesforce.">
    <a
      v-bind="salesforceLinkAttrs"
      data-testid="opportunities-empty-link"
      class="link"
      :class="{ 'cursor-not-allowed opacity-50': !salesforceUrl }">
      View in Salesforce
    </a>
  </EmptyDisplay>

  <div v-else class="flex flex-col gap-6 p-4">
    <div data-testid="opportunities-chips" class="flex flex-wrap gap-2">
      <span class="badge badge-lg badge-soft">
        <b>{{ summary.openCount }}</b>&nbsp;open
      </span>
      <span class="badge badge-lg badge-soft badge-primary">
        <b>{{ formatCurrency(summary.openWeightedRevenue, true) }}</b>&nbsp;pipeline (open)
      </span>
      <span class="badge badge-lg badge-soft badge-success">
        <b>{{ summary.wonCount }}</b>&nbsp;won
      </span>
      <span class="badge badge-lg badge-soft badge-error">
        <b>{{ summary.lostCount }}</b>&nbsp;lost
      </span>
    </div>

    <main class="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_2fr]">
      <section class="self-start overflow-hidden rounded-2xl border border-solid border-gray-100 p-4 shadow-md">
        <h2 class="text-base font-semibold">Pipeline by Product (open opportunities)</h2>

        <!-- FusionCharts given an empty dataset draws a blank canvas rather
             than failing visibly, so the panel is swapped out before it gets
             there. -->
        <div
          v-if="!hasChartData"
          data-testid="opportunities-chart-empty"
          class="flex h-[360px] flex-col items-center justify-center gap-1 text-center text-sm opacity-60">
          <span>{{ chartEmptyMessage }}.</span>
          <a
            v-bind="salesforceLinkAttrs"
            data-testid="opportunities-chart-empty-link"
            class="link"
            :class="{ 'cursor-not-allowed opacity-50': !salesforceUrl }">
            View in Salesforce
          </a>
        </div>

        <fusioncharts
          v-else
          data-testid="opportunities-chart"
          type="pie2d"
          width="100%"
          height="360"
          dataFormat="json"
          :dataSource="chartDataSource" />

        <p data-testid="opportunities-footer" class="mt-2 text-sm opacity-70">
          {{ summary.openCount }} open {{ openOpportunityNoun }} totaling
          {{ formatCurrency(summary.openWeightedRevenue, true) }} in weighted revenue.
        </p>
      </section>

      <section class="overflow-x-auto">
        <table data-testid="opportunities-table" class="apptable dense w-full">
          <thead>
            <tr>
              <td class="nosort">Product</td>
              <td class="nosort">Opportunity Owner</td>
              <td class="nosort text-right">Expected Revenue</td>
              <td class="nosort text-right">Weighted Revenue</td>
              <td class="nosort text-right">Probability</td>
              <td class="nosort">Close Date</td>
              <td class="nosort">Status</td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in visibleRows" :key="o.id">
              <td>{{ o.product }}</td>
              <td>{{ dash(o.ownerName) }}</td>
              <td class="text-right">{{ dash(formatCurrency(o.expectedRevenue, true)) }}</td>
              <td class="text-right">{{ dash(formatCurrency(o.weightedRevenue, true)) }}</td>
              <td class="text-right">{{ o.probability === null ? '—' : `${o.probability}%` }}</td>
              <td>{{ dash(formatDate(o.closeDate ?? undefined)) }}</td>
              <td><span :class="statusClass(o)">{{ opportunityStatus(o) }}</span></td>
            </tr>
          </tbody>
        </table>

        <button
          v-if="isCapped && !showAll"
          data-testid="opportunities-show-all"
          class="btn btn-sm btn-ghost mt-2"
          @click="showAll = true">
          Show all {{ rows.length }} opportunities
        </button>
      </section>
    </main>
  </div>
</template>
